import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, CreditCard, Loader2, Sparkles } from 'lucide-react';
import { useUiLanguage } from '@/lib/i18n';
import { apiMerchantDashboard } from '@/services/api';
import { AI_ACTION_TYPES, executeAIAction } from '@/lib/aiActionEngine';
import CenteredStatusPopup from '@/components/merchant/CenteredStatusPopup';

function pickBestPoints(pointsValue) {
  const points = Math.max(Number(pointsValue || 0), 0);
  const tiers = [2500, 1000, 500, 300, 100];
  for (const tier of tiers) {
    if (points >= tier) return tier;
  }
  return 0;
}

export default function RedemptionsTapPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useUiLanguage();

  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [stage, setStage] = useState('idle');
  const [message, setMessage] = useState('');
  const [toast, setToast] = useState('');
  const autoRunTriggeredRef = useRef(false);
  const closeTimerRef = useRef(null);
  const toastTimerRef = useRef(null);

  const preferredFromState = useMemo(() => {
    const state = location?.state || {};
    if (!state || typeof state !== 'object') return null;
    const customerId = String(state.customerId || '').trim();
    if (!customerId) return null;
    return {
      id: customerId,
      name: String(state.customerName || 'Customer'),
      points: Math.max(Number(state.customerPoints || 0), 0),
      applyBonusOnRedemption: Boolean(state.applyBonusOnRedemption),
    };
  }, [location?.state]);

  const preferredFromSession = useMemo(() => {
    try {
      const raw = sessionStorage.getItem('tap_redemption_customer');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const customerId = String(parsed?.customerId || '').trim();
      if (!customerId) return null;
      return {
        id: customerId,
        name: String(parsed?.customerName || 'Customer'),
        points: Math.max(Number(parsed?.customerPoints || 0), 0),
        applyBonusOnRedemption: Boolean(parsed?.applyBonusOnRedemption),
      };
    } catch {
      return null;
    }
  }, []);

  const preferredCustomerId = useMemo(() => {
    const fromQuery = String(searchParams.get('customer_id') || '').trim();
    if (fromQuery) return fromQuery;
    if (preferredFromState?.id) return preferredFromState.id;
    if (preferredFromSession?.id) return preferredFromSession.id;
    return '';
  }, [preferredFromSession?.id, preferredFromState?.id, searchParams]);

  useEffect(() => {
    let active = true;
    async function run() {
      setLoading(true);
      try {
        const dashboard = await apiMerchantDashboard();
        if (!active) return;
        setCustomers(Array.isArray(dashboard?.customers) ? dashboard.customers : []);
      } catch {
        if (!active) return;
        setCustomers([]);
      }
      if (active) setLoading(false);
    }
    run();
    return () => {
      active = false;
    };
  }, []);

  const selectedCustomer = useMemo(() => {
    const rows = customers || [];
    const preferredBonus =
      Boolean(preferredFromState?.id === preferredCustomerId && preferredFromState?.applyBonusOnRedemption)
      || Boolean(preferredFromSession?.id === preferredCustomerId && preferredFromSession?.applyBonusOnRedemption);
    if (!rows.length) {
      if (preferredFromState?.id && preferredFromState.id === preferredCustomerId) return preferredFromState;
      if (preferredFromSession?.id && preferredFromSession.id === preferredCustomerId) return preferredFromSession;
      return null;
    }
    if (preferredCustomerId) {
      const preferred = rows.find((c) => String(c?.id || '') === preferredCustomerId);
      if (preferred) return { ...preferred, applyBonusOnRedemption: preferredBonus };
    }
    const fallback = rows.find((c) => Number(c?.points || 0) >= 100) || rows[0] || null;
    return fallback ? { ...fallback, applyBonusOnRedemption: false } : null;
  }, [customers, preferredCustomerId, preferredFromSession, preferredFromState]);

  const runTapRedemption = useCallback(async () => {
    if (!selectedCustomer || stage === 'processing' || stage === 'success') return;

    const points = pickBestPoints(selectedCustomer.points);
    if (points < 100) {
      setStage('error');
      const msg = t('No rewards or points available');
      setMessage(`⚠️ ${msg}`);
      setToast(`❌ ${msg}`);
      closeTimerRef.current = window.setTimeout(() => {
        navigate('/merchant/redemptions', { replace: true });
      }, 5000);
      return;
    }

    setStage('processing');
    setMessage(`${t('Detecting customer')}...`);

    window.setTimeout(() => {
      setMessage(`${selectedCustomer.name} ${t('detected')}`);
    }, 180);

    try {
      const res = await executeAIAction(AI_ACTION_TYPES.REDEEM, {
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name,
        customerPoints: selectedCustomer.points,
        points,
        apply_bonus_on_redemption: Boolean(selectedCustomer?.applyBonusOnRedemption),
        type: 'BEST_AVAILABLE',
      });
      setStage('success');
      setMessage(`✅ ${res.message}`);
      setToast(`✅ ${res.message}`);
      closeTimerRef.current = window.setTimeout(() => {
        navigate('/merchant/redemptions', { replace: true });
      }, 5000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setStage('error');
      const msg = typeof detail === 'string' ? detail : t('Unable to redeem right now.');
      setMessage(`❌ ${msg}`);
      setToast(`❌ ${msg}`);
      closeTimerRef.current = window.setTimeout(() => {
        navigate('/merchant/redemptions', { replace: true });
      }, 5000);
    }
  }, [navigate, selectedCustomer, stage, t]);

  useEffect(() => {
    if (loading || !selectedCustomer || autoRunTriggeredRef.current) return;
    autoRunTriggeredRef.current = true;
    const timer = window.setTimeout(() => runTapRedemption(), 120);
    return () => window.clearTimeout(timer);
  }, [loading, runTapRedemption, selectedCustomer]);

  useEffect(() => {
    if (!String(toast || '').trim()) return undefined;
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    toastTimerRef.current = window.setTimeout(() => setToast(''), 3800);
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, [toast]);

  useEffect(() => () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  }, []);

  return (
    <div className="min-h-[75vh] rounded-2xl border border-stone-200 bg-white p-6 flex flex-col items-center justify-center text-center" data-testid="redemptions-tap-page">
      <CenteredStatusPopup message={toast} />
      <p className="text-xs uppercase tracking-wider text-brand font-semibold">{t('Tap Simulation')}</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
        {t('Tap Customer Card')}
      </h1>

      <motion.button
        type="button"
        onClick={runTapRedemption}
        animate={{ scale: stage === 'processing' ? [1, 1.06, 1] : [1, 1.03, 1] }}
        transition={{ repeat: Infinity, duration: 1.7, ease: 'easeInOut' }}
        className="mt-8 h-52 w-52 rounded-full border border-brand/30 bg-gradient-to-br from-brand-soft to-white shadow-inner inline-flex flex-col items-center justify-center gap-2"
        data-testid="redemptions-tap-circle"
      >
        {stage === 'processing' ? <Loader2 className="w-8 h-8 text-brand animate-spin" /> : <CreditCard className="w-8 h-8 text-brand" />}
        <span className="text-sm font-semibold text-brand">{t('Tap')}</span>
      </motion.button>

      <p className="mt-6 text-sm text-stone-600 min-h-[24px]">{loading ? t('Loading customers...') : message || t('Waiting for tap...')}</p>

      {stage === 'success' ? (
        <p className="mt-2 text-sm font-semibold text-emerald-600 inline-flex items-center gap-1">
          <CheckCircle2 className="w-4 h-4" /> {t('Redeemed successfully')}
        </p>
      ) : null}

      {stage === 'error' ? (
        <button type="button" onClick={() => navigate('/merchant/redemptions', { replace: true })} className="mt-4 h-10 px-4 rounded-xl border border-stone-200 bg-white text-sm font-semibold">
          {t('Back to Redemptions')}
        </button>
      ) : null}

      <p className="mt-6 text-xs text-stone-500 inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-brand" /> {t('Auto-closing in 5 seconds after success')}</p>
    </div>
  );
}
